import React, { createContext, useContext, useState } from 'react';
import type { ArticleAuthor } from '@/types/articles';

interface ArticleAuthorContextType {
  isOpen: boolean;
  editingAuthor: ArticleAuthor | null;
  brandId: string | null;
  openAddAuthor: (brandId: string) => void;
  openEditAuthor: (author: ArticleAuthor, brandId: string) => void;
  closeAuthorDialog: () => void;
}

const ArticleAuthorContext = createContext<ArticleAuthorContextType | undefined>(undefined);

export const ArticleAuthorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<ArticleAuthor | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);

  const openAddAuthor = (brandId: string) => {
    setEditingAuthor(null);
    setBrandId(brandId);
    setIsOpen(true);
  };

  const openEditAuthor = (author: ArticleAuthor, brandId: string) => {
    setEditingAuthor(author);
    setBrandId(brandId);
    setIsOpen(true);
  };

  const closeAuthorDialog = () => {
    setIsOpen(false);
    setEditingAuthor(null);
  };

  return (
    <ArticleAuthorContext.Provider 
      value={{ 
        isOpen, 
        editingAuthor, 
        brandId,
        openAddAuthor, 
        openEditAuthor, 
        closeAuthorDialog 
      }}
    >
      {children}
    </ArticleAuthorContext.Provider>
  );
};

export const useArticleAuthorDialog = () => {
  const context = useContext(ArticleAuthorContext);
  if (context === undefined) {
    throw new Error('useArticleAuthorDialog must be used within an ArticleAuthorProvider');
  }
  return context;
};
